import React from "react";
import PropTypes from "prop-types";
import "./w86SegmentedTabs.css";

/**
 * shadcn-style segmented tab strip. Owns the role/aria wiring in one place so the
 * three former hand-rolled copies (form builder, review task, detail pane) can't
 * drift. `tabs` is an array of { value, label }; the active tab is matched by
 * `value` (a string key or numeric index).
 *
 * Variants:
 *  - "inline" (default): bare tablist.
 *  - "fill": full-width compact tablist (`--fill --compact`).
 *  - "bar": tablist wrapped in a full-width header bar (`--bar`).
 */
export default function W86SegmentedTabs({
  tabs,
  value,
  onChange,
  variant = "inline",
  ariaLabel,
}) {
  const listClassName = [
    "w86-segmented-tabs",
    variant === "fill" ? "w86-segmented-tabs--fill w86-segmented-tabs--compact" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const list = (
    <div className={listClassName} role="tablist" aria-label={ariaLabel}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={value === tab.value}
          className={`w86-segmented-tab${value === tab.value ? " w86-segmented-tab--active" : ""}`}
          onClick={() => onChange(tab.value)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  if (variant === "bar") {
    return <div className="w86-segmented-tabs--bar">{list}</div>;
  }
  return list;
}

W86SegmentedTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["inline", "fill", "bar"]),
  ariaLabel: PropTypes.string,
};
